import { Badge } from '@/components/ui/Badge'
import { useTranslation } from 'react-i18next'
import { initialsOf } from '@/lib/normalize'
import { enumLabel } from '@/lib/enumLabel'
import { useLocaleFormat } from '@/hooks/useLocaleFormat'
import type { User } from '@/types'

interface UserDetailPanelProps {
  user: User
  businessName?: string
  onClose: () => void
}

export function UserDetailPanel({ user, businessName }: UserDetailPanelProps) {
  const { t } = useTranslation()
  const { formatDate, formatDateTime } = useLocaleFormat()
  const roleBadgeVariant =
    user.role === 'admin' ? 'info' : user.role === 'caregiver' ? 'success' : 'default'

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-100 text-lg font-bold text-primary-700">
          {initialsOf(user.displayName)}
        </div>
        <div className="min-w-0">
          <h4 className="break-words text-lg font-semibold text-gray-900">{user.displayName}</h4>
          <p className="break-all text-sm text-gray-500">{user.email}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 rounded-lg bg-gray-50 p-4 sm:grid-cols-2">
        <div>
          <p className="text-xs text-gray-500">{t('users.detail.role')}</p>
          <Badge variant={roleBadgeVariant}>{enumLabel(t, 'roles', user.role)}</Badge>
        </div>
        <div>
          <p className="text-xs text-gray-500">{t('users.detail.status')}</p>
          <Badge variant={user.isActive ? 'success' : 'danger'}>
            {user.isActive ? t('common.active') : t('common.disabled')}
          </Badge>
        </div>
        <div>
          <p className="text-xs text-gray-500">{t('users.detail.business')}</p>
          <p className="break-all text-sm font-medium text-gray-900">
            {businessName ?? (user.businessId || t('common.notAvailable'))}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">{t('users.detail.subscription')}</p>
          <p className="text-sm font-medium text-gray-900">{enumLabel(t, 'tiers', user.subscriptionTier ?? 'none')}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">{t('users.detail.created')}</p>
          <p className="text-sm text-gray-900">
            {user.createdAt ? formatDate(user.createdAt.toDate()) : t('common.notAvailable')}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">{t('users.detail.lastLogin')}</p>
          <p className="text-sm text-gray-900">
            {user.lastLogin ? formatDateTime(user.lastLogin.toDate()) : t('common.never')}
          </p>
        </div>
      </div>

      {user.requiresPasswordChange && (
        <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-700">
          {t('users.detail.requiresPasswordChange')}
        </div>
      )}
    </div>
  )
}
